import { useAccount, useMsal } from '@azure/msal-react';
import { Avatar, List, ListItem, ListItemAvatar, ListItemText, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { msalInstance } from '@config/authConfig';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { SERVICES } from '@shared/constants/apiConfig';
import { Roles } from '@shared/constants/constants';
import { useUserAccount } from '@shared/hooks/useUserAccount';
import { roundRobinAlertToken, setRoundRobinAlertToken } from '@store/reducers/roundRobinSlice';
import { persistor } from '@store/store';
import { DrawerHeader } from '@styles/Layout/SidebarStyles';
import { colorTheme } from '@styles/muiTheme';
import { DealsButton } from '@styles/StyledButtons';

import { useTabs } from '@shared/hooks/useTabs';
import { requestAddRoundRobin, requestRemoveRoundRobin, requestAddSDRRoundRobin, requestRemoveSDRRoundRobin, requestAddTitlesRoundRobin, requestRemoveTitlesRoundRobin } from './request';

export const SidebarHead = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const alertToken = useSelector(roundRobinAlertToken);
  const { clearClosedTabs } = useTabs();

  const { userHasRoles } = useUserAccount();
  const { accounts } = useMsal();

  const account = useAccount(accounts[0] || {});
  const isLpUser = userHasRoles([Roles.LP]);
  const isSDRUser = userHasRoles([Roles.SDR]);
  const isTitlesUser = userHasRoles([Roles.TITLES]);

  const [showAlert, setShowAlert] = useState(false);
  const [loading, setLoading] = useState(false);

  const { data: lpData, mutate: lpMutate } = useSWR<boolean>(`/${SERVICES.USER}/round-robin/is-logged-in`);
  const { data: sdrData, mutate: sdrMutate } = useSWR<boolean>(`/${SERVICES.USER}/round-robin/sdr/is-logged-in`);
  const { data: titlesData, mutate: titlesMutate } = useSWR<boolean>(`/${SERVICES.USER}/round-robin/titles/is-logged-in`);

  useEffect(() => {
    setShowAlert((isLpUser || isSDRUser || isTitlesUser) && !alertToken && (lpData || sdrData) === false);
  }, [isLpUser, isSDRUser, isTitlesUser, alertToken, lpData]);

  const initials = account?.name
    ?.split(' ')
    .map((name) => name[0])
    .join('');

  const logOff = useCallback(async () => {
    if (lpData || sdrData || titlesData) {
      dispatch(setRoundRobinAlertToken(null));
      if (lpData) {
        await requestRemoveRoundRobin(true);
      } 
      if (sdrData) {
        await requestRemoveSDRRoundRobin(true);
      } 
      if (titlesData) {
        await requestRemoveTitlesRoundRobin(true);
      }
    }

    clearClosedTabs();

    persistor.purge();
    // Reset URL after logout
    navigate('/'); // Redirect to default URL

    msalInstance.logoutRedirect().catch((e) => {
      console.error(e);
    });
  }, [persistor, msalInstance, lpData, sdrData, titlesData]);

  const logOffRoundRobin = useCallback(async () => {
    setLoading(true);
    await requestRemoveRoundRobin();
    await lpMutate();
    if (!alertToken) dispatch(setRoundRobinAlertToken('NotIncluded'));
    setLoading(false);
  }, [alertToken]);

  const loginRoundRobin = useCallback(async () => {
    setLoading(true);
    await requestAddRoundRobin();
    await lpMutate();
    if (!alertToken) dispatch(setRoundRobinAlertToken('Included'));
    setLoading(false);
  }, [alertToken]);

  const handleAlertClose = useCallback(() => {
    setLoading(true);
    dispatch(setRoundRobinAlertToken('NotIncluded'));
    setShowAlert(false);
    setLoading(false);
  }, [alertToken]);

  const logOffRoundRobinSDR = useCallback(async () => {
    setLoading(true);
    await requestRemoveSDRRoundRobin();
    await sdrMutate();
    if (!alertToken) dispatch(setRoundRobinAlertToken('NotIncluded'));
    setLoading(false);
  }, [alertToken]);

  const loginRoundRobinSDR = useCallback(async () => {
    setLoading(true);
    await requestAddSDRRoundRobin();
    await sdrMutate();
    if (!alertToken) dispatch(setRoundRobinAlertToken('Included'));
    setLoading(false);
  }, [alertToken]);

  const logOffRoundRobinTitles = useCallback(async () => {
    setLoading(true);
    await requestRemoveTitlesRoundRobin();
    await titlesMutate();
    if (!alertToken) dispatch(setRoundRobinAlertToken('NotIncluded'));
    setLoading(false);
  }, [alertToken]);

  const loginRoundRobinTitles = useCallback(async () => {
    setLoading(true);
    await requestAddTitlesRoundRobin();
    await titlesMutate();
    if (!alertToken) dispatch(setRoundRobinAlertToken('Included'));
    setLoading(false);
  }, [alertToken]);

  return (
    <>
      {showAlert && (
        <AlertModal
          open
          title="Round Robin"
          subtitle="Do you want to Log Into Round Robin?"
          isLoading={loading}
          buttonText="Yes"
          cancelButtonText="No"
          onClose={handleAlertClose}
          onConfirm={isLpUser ? loginRoundRobin : isSDRUser ? loginRoundRobinSDR : isTitlesUser ? loginRoundRobinTitles : () => null}
        />
      )}

      <DrawerHeader>
        <List sx={{ width: '100%' }}>
          <ListItem disablePadding>
            <ListItemAvatar>
              <Avatar sx={{ bgcolor: colorTheme.palette.primary.light, marginRight: '4px' }}>{initials}</Avatar>
            </ListItemAvatar>
            <ListItemText>
              <Typography variant="h4">{account?.name}</Typography>
            </ListItemText>
          </ListItem>
          <ListItem
            disablePadding
            sx={{ gap: 8.3, marginTop: '8px', justifyContent: 'flex-end' }}
          >
            {(isLpUser || isSDRUser || isTitlesUser) && (
              <>
                {(lpData || sdrData || titlesData) ? (
                  <DealsButton
                    variant="contained"
                    color="error"
                    size="small"
                    loading={loading}
                    onClick={(lpData && isLpUser) ? logOffRoundRobin : (sdrData && isSDRUser) ? logOffRoundRobinSDR : (titlesData && isTitlesUser) ? logOffRoundRobinTitles : () => null}
                  >
                    Round Robin
                  </DealsButton>
                ) : (
                  <DealsButton
                    variant="contained"
                    color="success"
                    size="small"
                    loading={loading}
                    onClick={(!lpData && isLpUser) ? loginRoundRobin : (!sdrData && isSDRUser) ? loginRoundRobinSDR : (!titlesData && isTitlesUser) ? loginRoundRobinTitles : () => null}
                  >
                    Round Robin
                  </DealsButton>
                )}
              </>
            )}
            <DealsButton
              variant="contained"
              color="warning"
              size="small"
              onClick={logOff}
            >
              Log off
            </DealsButton>
          </ListItem>
        </List>
      </DrawerHeader>
    </>
  );
};
